import React, {useState, useEffect} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import toast from 'react-hot-toast';
import {useMutation} from 'react-query';
import {Eye, EyeOff} from 'react-feather';

import '../../index.css';

import {Input, Button} from '@components';
import {resetPasswordApi} from '@apis';

import logo from '@images/nls-logo.png';

const ResetPassword = () => {
  const {t} = useTranslation();
  const [state, setState] = useState({
    password: '',
    confirmPassword: '',
  });
  const [signupPasswordVisibility, setSignupPasswordVisibility] = useState(
    false,
  );
  const [signupCPasswordVisibility, setSignupCPasswordVisibility] = useState(
    false,
  );

  const history = useHistory();
  const location = useLocation();

  const resetPassword = useMutation(params => resetPasswordApi(params));

  useEffect(() => {
    if (resetPassword?.isError) {
      if (typeof resetPassword?.error === 'string') {
        toast.error(resetPassword?.error);
      } else {
        toast.error(t('auth.resetPasswordError'));
      }
      resetPassword?.reset();
    }

    if (resetPassword?.isSuccess) {
      toast.success(t('auth.passwordResetSuccess'));
      history.replace('/');
    }
  }, [history, resetPassword, t]);

  const handleResetPassword = async e => {
    e.preventDefault();
    const {password, confirmPassword} = state;
    if (password !== confirmPassword) {
      await toast.error(t('auth.passwordMatch'));
      return false;
    } else {
      const token = new URLSearchParams(location.search).get('token');
      const email = new URLSearchParams(location.search).get('email');

      if (!token || !email) {
        await toast.error(t('auth.invalidResetLink'));
        return false;
      }

      await resetPassword.mutate({
        token,
        email,
        new_password: password,
      });
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleResetPassword(e);
    }
  };

  return (
    <div className="loginContainer">
      <div className="loginWindow flex justify-center items-center">
        <div
          style={{width: 494}}
          className="borderRadiusLeft5 flex flex-col flex-auto items-center justify-center next-gradient h-screen p-16 relative">
          <div className="w-full items-center my-4 px-20">
            <h1 className="font-bold text-3xl text-white text-center">
              {t('auth.welcome')}
            </h1>
            <p className="font-light text-xs text-white text-center py-3">
              {t('auth.resetPasswordTitle')}
            </p>
          </div>
          <div className="w-full items-center">
            <div className="position-rel">
              <Input
                dark
                white
                type={signupPasswordVisibility ? 'text' : 'password'}
                value={state.password}
                onChange={e => setState({...state, password: e.target.value})}
                label={t('auth.password')}
                placeholder={t('auth.enterPassword')}
              />
              <div
                className="password-toggle"
                onClick={() => {
                  setSignupPasswordVisibility(!signupPasswordVisibility);
                }}>
                {signupPasswordVisibility ? (
                  <Eye size="14" color="black" />
                ) : (
                  <EyeOff size="14" color="black" />
                )}
              </div>
            </div>

            <div className="position-rel">
              <Input
                dark
                white
                type={signupCPasswordVisibility ? 'text' : 'password'}
                value={state.confirmPassword}
                onChange={e =>
                  setState({...state, confirmPassword: e.target.value})
                }
                label={t('auth.confirmPassword')}
                placeholder={t('auth.confirmPassword')}
                onKeyDown={handleKeyDown}
              />
              <div
                className="password-toggle"
                onClick={() => {
                  setSignupCPasswordVisibility(!signupCPasswordVisibility);
                }}>
                {signupCPasswordVisibility ? (
                  <Eye size="14" color="black" />
                ) : (
                  <EyeOff size="14" color="black" />
                )}
              </div>
            </div>
          </div>
          <div
            className="w-full inset-x-0 bottom-14"
            style={{margin: '0 60px'}}>
            <Button
              block
              onClick={handleResetPassword}
              loading={resetPassword?.isLoading}
              disabled={
                resetPassword?.isLoading ||
                !state.password ||
                !state.confirmPassword
              }
              label={t('auth.resetPassword')}
            />
            <div className="flex flex-row justify-center items center my-5">
              <p className="text-white text-xs">
                {t('auth.alreadyHaveAccount')}
              </p>
              <Link to="/" className="text-white text-xs px-2 underline">
                {t('auth.signInNow')}
              </Link>
            </div>
          </div>
        </div>
        <div
          style={{width: 400}}
          className="flex borderRadiusRight5 justify-center items-center next-gradient h-screen p-4">
          <img
            className="object-contain h-80 loginLogo"
            src={logo}
            alt="Next Level Technology Logo"
          />
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
